import {
  FaqCategory,
  FaqData,
  FaqGroup,
  FaqValidationIssue,
  PageFaqGroup,
  SchemaFaqPage,
  SchemaQuestion,
} from '@/types/faq'

export function filterFaqGroupsByScene(
  data: FaqData,
  scene: string,
): readonly FaqGroup[] {
  return data.groups.filter(group => group.scenes.includes(scene))
}

export function toFaqCategories(
  groups: readonly FaqGroup[],
): readonly FaqCategory[] {
  return groups.map(group => ({
    id: group.id,
    title: group.prefix && group.prefix.trim() ? `${group.prefix.trim()}-${group.title}` : group.title,
    items: group.items.map(item => ({ ...item })),
  }))
}

export function toPageFaqGroups(
  groups: readonly FaqGroup[],
): readonly PageFaqGroup[] {
  return groups.map(group => ({
    id: group.id,
    title: group.title,
    items: group.items.map(item => ({
      id: item.id,
      question: item.question,
      answer: item.answer,
    })),
  }))
}

export function createFaqPageSchema(
  groups: readonly FaqGroup[],
): SchemaFaqPage {
  const mainEntity = groups.reduce<SchemaQuestion[]>((questions, group) => {
    group.items.forEach(item => {
      questions.push({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })
    })
    return questions
  }, [])

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity,
  }
}

type UnknownRecord = { [key: string]: unknown }

interface IssueContext {
  readonly groupId?: string;
  readonly itemId?: string;
  readonly blockType?: string;
}

interface IdentifierOccurrence extends IssueContext {
  readonly path: string;
}

const BLOCK_TYPES = [
  'paragraph',
  'list',
  'ordered-list',
  'table',
  'related',
]

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function getString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

function addIssue(
  issues: FaqValidationIssue[],
  code: string,
  path: string,
  message: string,
  context: IssueContext = {},
): void {
  issues.push({ code, path, message, ...context })
}

function validateRequiredString(
  value: unknown,
  path: string,
  fieldName: string,
  issues: FaqValidationIssue[],
  context: IssueContext,
): void {
  if (!isNonEmptyString(value)) {
    addIssue(
      issues,
      'invalid-required-string',
      path,
      `${fieldName} must be a non-empty string.`,
      context,
    )
  }
}

function validateStringArray(
  value: unknown,
  path: string,
  fieldName: string,
  issues: FaqValidationIssue[],
  context: IssueContext,
): void {
  if (!Array.isArray(value) || value.length === 0) {
    addIssue(
      issues,
      'invalid-non-empty-array',
      path,
      `${fieldName} must be a non-empty array.`,
      context,
    )
    return
  }

  value.forEach((entry, index) => {
    validateRequiredString(
      entry,
      `${path}[${index}]`,
      `${fieldName} entry`,
      issues,
      context,
    )
  })
}

function validateTableBlock(
  block: UnknownRecord,
  path: string,
  issues: FaqValidationIssue[],
  context: IssueContext,
): void {
  validateStringArray(
    block.headers,
    `${path}.headers`,
    'Table headers',
    issues,
    context,
  )

  if (!Array.isArray(block.rows) || block.rows.length === 0) {
    addIssue(
      issues,
      'invalid-table-rows',
      `${path}.rows`,
      'Table rows must be a non-empty array.',
      context,
    )
    return
  }

  block.rows.forEach((row, rowIndex) => {
    const rowPath = `${path}.rows[${rowIndex}]`
    if (!Array.isArray(row) || row.length === 0) {
      addIssue(
        issues,
        'invalid-table-row',
        rowPath,
        'Each table row must be a non-empty array.',
        context,
      )
      return
    }

    row.forEach((cell, cellIndex) => {
      if (typeof cell !== 'string') {
        addIssue(
          issues,
          'invalid-table-cell',
          `${rowPath}[${cellIndex}]`,
          'Each table cell must be a string.',
          context,
        )
      }
    })
  })
}

function validateRelatedBlock(
  block: UnknownRecord,
  path: string,
  issues: FaqValidationIssue[],
  context: IssueContext,
): void {
  if (!Array.isArray(block.links) || block.links.length === 0) {
    addIssue(
      issues,
      'invalid-related-links',
      `${path}.links`,
      'Related links must be a non-empty array.',
      context,
    )
    return
  }

  block.links.forEach((link, linkIndex) => {
    const linkPath = `${path}.links[${linkIndex}]`
    if (!isRecord(link)) {
      addIssue(
        issues,
        'invalid-related-link',
        linkPath,
        'Each related link must be an object.',
        context,
      )
      return
    }

    validateRequiredString(
      link.text,
      `${linkPath}.text`,
      'Related link text',
      issues,
      context,
    )
    validateRequiredString(
      link.url,
      `${linkPath}.url`,
      'Related link URL',
      issues,
      context,
    )
  })
}

function validateBlock(
  value: unknown,
  path: string,
  issues: FaqValidationIssue[],
  groupId?: string,
  itemId?: string,
): void {
  if (!isRecord(value)) {
    addIssue(
      issues,
      'invalid-block',
      path,
      'FAQ content block must be an object.',
      { groupId, itemId },
    )
    return
  }

  const blockType = getString(value.type)
  const context = { groupId, itemId, blockType }

  if (!blockType || BLOCK_TYPES.indexOf(blockType) === -1) {
    addIssue(
      issues,
      'invalid-block-type',
      `${path}.type`,
      'FAQ content block type must be one of paragraph, list, ordered-list, table, or related.',
      context,
    )
    return
  }

  if (blockType === 'paragraph') {
    validateRequiredString(
      value.text,
      `${path}.text`,
      'Paragraph text',
      issues,
      context,
    )
    return
  }

  if (blockType === 'list' || blockType === 'ordered-list') {
    validateStringArray(
      value.items,
      `${path}.items`,
      'List items',
      issues,
      context,
    )
    return
  }

  if (blockType === 'table') {
    validateTableBlock(value, path, issues, context)
    return
  }

  validateRelatedBlock(value, path, issues, context)
}

function recordIdentifier(
  occurrences: Map<string, IdentifierOccurrence[]>,
  id: unknown,
  path: string,
  context: IssueContext,
): void {
  if (typeof id !== 'string') return

  const existing = occurrences.get(id) || []
  existing.push({ path, ...context })
  occurrences.set(id, existing)
}

function addDuplicateIssues(
  issues: FaqValidationIssue[],
  occurrences: Map<string, IdentifierOccurrence[]>,
  code: string,
  label: string,
): void {
  occurrences.forEach((entries, id) => {
    if (entries.length < 2) return

    entries.forEach(entry => {
      addIssue(
        issues,
        code,
        entry.path,
        `${label} ID "${id}" is duplicated.`,
        entry,
      )
    })
  })
}

export function validateFaqData(value: unknown): FaqValidationIssue[] {
  const issues: FaqValidationIssue[] = []

  if (!isRecord(value)) {
    addIssue(
      issues,
      'invalid-root',
      '$',
      'FAQ data root must be an object containing a groups array.',
    )
    return issues
  }

  Object.keys(value).forEach(key => {
    if (key !== 'groups') {
      addIssue(
        issues,
        'unexpected-root-field',
        `$.${key}`,
        `FAQ data root must not contain the field "${key}".`,
      )
    }
  })

  if (!Array.isArray(value.groups)) {
    addIssue(
      issues,
      'invalid-groups',
      '$.groups',
      'FAQ data groups must be an array.',
    )
    return issues
  }

  const groupIds = new Map<string, IdentifierOccurrence[]>()
  const itemIds = new Map<string, IdentifierOccurrence[]>()

  value.groups.forEach((group, groupIndex) => {
    const groupPath = `$.groups[${groupIndex}]`
    if (!isRecord(group)) {
      addIssue(
        issues,
        'invalid-group',
        groupPath,
        'FAQ group must be an object.',
      )
      return
    }

    const groupId = getString(group.id)
    const groupContext = { groupId }

    validateRequiredString(
      group.id,
      `${groupPath}.id`,
      'FAQ group ID',
      issues,
      groupContext,
    )
    recordIdentifier(groupIds, group.id, `${groupPath}.id`, groupContext)
    validateRequiredString(
      group.title,
      `${groupPath}.title`,
      'FAQ group title',
      issues,
      groupContext,
    )
    if (typeof group.prefix !== 'undefined' && typeof group.prefix !== 'string') {
      addIssue(issues, 'invalid-prefix', `${groupPath}.prefix`, 'FAQ group prefix must be a string.', groupContext)
    }
    validateStringArray(
      group.scenes,
      `${groupPath}.scenes`,
      'FAQ group scenes',
      issues,
      groupContext,
    )

    if (!Array.isArray(group.items)) {
      addIssue(
        issues,
        'invalid-items',
        `${groupPath}.items`,
        'FAQ group items must be an array.',
        groupContext,
      )
      return
    }

    group.items.forEach((item, itemIndex) => {
      const itemPath = `${groupPath}.items[${itemIndex}]`
      if (!isRecord(item)) {
        addIssue(
          issues,
          'invalid-item',
          itemPath,
          'FAQ item must be an object.',
          groupContext,
        )
        return
      }

      const itemId = getString(item.id)
      const itemContext = { groupId, itemId }

      validateRequiredString(
        item.id,
        `${itemPath}.id`,
        'FAQ item ID',
        issues,
        itemContext,
      )
      recordIdentifier(itemIds, item.id, `${itemPath}.id`, itemContext)
      validateRequiredString(
        item.question,
        `${itemPath}.question`,
        'FAQ item question',
        issues,
        itemContext,
      )
      validateRequiredString(
        item.answer,
        `${itemPath}.answer`,
        'FAQ item answer',
        issues,
        itemContext,
      )

      if (typeof item.blocks === 'undefined') return

      if (!Array.isArray(item.blocks) || item.blocks.length === 0) {
        addIssue(
          issues,
          'invalid-blocks',
          `${itemPath}.blocks`,
          'FAQ item blocks must be a non-empty array when provided.',
          itemContext,
        )
        return
      }

      item.blocks.forEach((block, blockIndex) => {
        validateBlock(
          block,
          `${itemPath}.blocks[${blockIndex}]`,
          issues,
          groupId,
          itemId,
        )
      })
    })
  })

  addDuplicateIssues(
    issues,
    groupIds,
    'duplicate-group-id',
    'FAQ group',
  )
  addDuplicateIssues(issues, itemIds, 'duplicate-item-id', 'FAQ item')

  return issues
}
